import { Archive, ArchiveRestore, Check, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { categoryPalette, type CategoryColor } from '@tokens';

import { Button, CATEGORY_ICONS, CategoryBadge, Drawer, useCategoryColors, useToast } from '@/components/ui';
import { useApp } from '@/lib/hooks';
import { liveCategories } from '@/lib/merge';
import { deleteCategory, saveCategory, setCategoryArchived } from '@/lib/store';
import type { Category } from '@/lib/types';

export function Categories() {
  const { data } = useApp();
  const [editing, setEditing] = useState<Category | 'new' | null>(null);
  const all = liveCategories(data);
  const counts = new Map<string, number>();
  for (const e of data.expenses.values()) if (!e.deletedAt) counts.set(e.categoryId, (counts.get(e.categoryId) ?? 0) + 1);

  const section = (title: string, items: Category[]) =>
    items.length > 0 && (
      <section className="card" style={{ padding: 8 }}>
        <div className="faint" style={{ fontSize: 12, fontWeight: 600, padding: '8px 10px' }}>{title.toUpperCase()}</div>
        {items.map((c) => (
          <button key={c.id} className="list-row" onClick={() => setEditing(c)}>
            <CategoryBadge icon={c.icon} color={c.color} />
            <span className="grow" style={{ fontWeight: 500 }}>{c.name}</span>
            <span className="muted num">{counts.get(c.id) ?? 0}</span>
          </button>
        ))}
      </section>
    );

  return (
    <div className="page" style={{ maxWidth: 760 }}>
      <div className="page-head">
        <h1>Categories</h1>
        <Button icon={Plus} onClick={() => setEditing('new')}>New category</Button>
      </div>
      <div className="grid">
        {section(`Active · ${all.filter((c) => !c.isArchived).length}`, all.filter((c) => !c.isArchived))}
        {section('Archived', all.filter((c) => c.isArchived))}
        <p className="muted" style={{ margin: 0 }}>Archived categories are hidden when adding expenses but stay on past expenses.</p>
      </div>
      {editing && <Editor category={editing === 'new' ? null : editing} count={editing === 'new' ? 0 : counts.get(editing.id) ?? 0} others={all.filter((c) => !c.isArchived && (editing === 'new' || c.id !== editing.id))} onClose={() => setEditing(null)} />}
    </div>
  );
}

function Swatch({ color, selected, onPick }: { color: CategoryColor; selected: boolean; onPick: () => void }) {
  const c = useCategoryColors(color);
  return (
    <button onClick={onPick} aria-label={color} aria-pressed={selected} style={{ width: 40, height: 40, borderRadius: 999, background: c.bg, border: `2px solid ${selected ? c.fg : 'transparent'}`, display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
      {selected ? <Check size={18} color={c.fg} /> : <span style={{ width: 14, height: 14, borderRadius: 999, background: c.fg }} />}
    </button>
  );
}

function Editor({ category, count, others, onClose }: { category: Category | null; count: number; others: Category[]; onClose: () => void }) {
  const toast = useToast();
  const { data } = useApp();
  const [name, setName] = useState(category?.name ?? '');
  const [icon, setIcon] = useState(category?.icon ?? 'shopping-bag');
  const [color, setColor] = useState<CategoryColor>((category?.color as CategoryColor) ?? 'blue');
  const [moveTo, setMoveTo] = useState(others[0]?.id ?? '');
  const [error, setError] = useState<string>();
  const accent = useCategoryColors(color);

  const save = () => {
    const trimmed = name.trim();
    if (!trimmed) return setError('Name is required');
    const clash = liveCategories(data).some((c) => c.id !== category?.id && c.name.toLowerCase() === trimmed.toLowerCase());
    if (clash) return setError('A category with this name already exists');
    saveCategory({ name: trimmed, icon, color }, category?.id);
    onClose();
    toast({ message: category ? 'Category updated' : 'Category added' });
  };

  const totalUses = category ? [...data.expenses.values()].filter((e) => e.categoryId === category.id).length : 0;

  return (
    <Drawer
      title={category ? 'Edit category' : 'New category'}
      onClose={onClose}
      footer={<Button block size="lg" onClick={save}>Save</Button>}>
      <label className="field">
        <span>Name</span>
        <input className={`input ${error ? 'invalid' : ''}`} value={name} maxLength={30} onChange={(e) => (setName(e.target.value), setError(undefined))} autoFocus />
        {error && <span className="error-text">{error}</span>}
      </label>
      <div className="field">
        <span>Icon</span>
        <div className="chips">
          {Object.entries(CATEGORY_ICONS).map(([key, Icon]) => (
            <button key={key} onClick={() => setIcon(key)} aria-label={key.replace(/-/g, ' ')} aria-pressed={icon === key}
              style={{ width: 42, height: 42, borderRadius: 12, cursor: 'pointer', display: 'grid', placeItems: 'center', background: icon === key ? accent.bg : 'var(--surface-muted)', border: `1.5px solid ${icon === key ? accent.fg : 'transparent'}`, color: icon === key ? accent.fg : 'var(--text-2)' }}>
              <Icon size={19} />
            </button>
          ))}
        </div>
      </div>
      <div className="field">
        <span>Color</span>
        <div className="chips">
          {(Object.keys(categoryPalette) as CategoryColor[]).map((k) => <Swatch key={k} color={k} selected={k === color} onPick={() => setColor(k)} />)}
        </div>
      </div>
      {category && (
        <div className="grid" style={{ borderTop: '1px solid var(--border)', paddingTop: 18 }}>
          <Button variant="secondary" icon={category.isArchived ? ArchiveRestore : Archive} onClick={() => { setCategoryArchived(category.id, !category.isArchived); onClose(); }}>
            {category.isArchived ? 'Unarchive' : 'Archive'}
          </Button>
          {totalUses > 0 ? (
            <div className="grid" style={{ gap: 8 }}>
              <span className="muted" style={{ fontSize: 13 }}>Delete moves its {count || totalUses} expenses to:</span>
              <div className="row">
                <select className="select" value={moveTo} onChange={(e) => setMoveTo(e.target.value)} aria-label="Move expenses to">
                  {others.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
                <Button variant="danger" icon={Trash2} disabled={!moveTo} onClick={() => { if (confirm(`Delete "${category.name}" and move its expenses?`)) { deleteCategory(category.id, moveTo); onClose(); toast({ message: 'Category deleted' }); } }}>Delete</Button>
              </div>
            </div>
          ) : (
            <Button variant="ghost" icon={Trash2} onClick={() => { if (confirm(`Delete "${category.name}"?`)) { deleteCategory(category.id, null); onClose(); toast({ message: 'Category deleted' }); } }}>Delete category</Button>
          )}
        </div>
      )}
    </Drawer>
  );
}
